import { AxiosResponse } from 'axios';
import axiosInstance, { API_ROUTES } from '@/lib/http/httpRequest';
import {
    Alarm,
    AllSegmentsIncludingCustom,
    AssetsMostOwned,
    CountTradersResponse,
    CreateSegmentPayload,
    CryptoFiatStableMetricDaily,
    DailyTraderAccounts,
    HoldingsSummary,
    HourlyOpenPosition,
    LastOpenPosition,
    LastTraderAccount,
    OpenPositionMetricSummary,
    ScalperActivityData,
    SegmentCriteria,
    SegmentHoldingsSummary,
    TopCrypto,
    TopListAsset,
    UpdateSegmentPayload,
} from '@/state/intelligence2/intelligence2Types';

export const getHourlyOpenPositionMetric = async (
    hours: string,
): Promise<AxiosResponse<HourlyOpenPosition>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/positions/open/metrics/hourly`, {
        params: { hours: hours },
    });
export const getCountTraderAccounts = async (): Promise<AxiosResponse<LastTraderAccount>> =>
    axiosInstance.get(`/intelligence/accounts/count/last`);
export const getCountTraderAccountsDaily = async (
    days: string,
): Promise<AxiosResponse<DailyTraderAccounts>> =>
    axiosInstance.get(`/intelligence/accounts/count/daily`, {
        params: { days: days },
    });
export const getAllSegmentsIncludingCustom = async (): Promise<
    AxiosResponse<AllSegmentsIncludingCustom[]>
> => axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/segments`);

export const getOpenPositionMetric = async (): Promise<AxiosResponse<LastOpenPosition>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/positions/open/metrics/last`);

export const getTop10TradedAssets = async (): Promise<AxiosResponse<TopListAsset>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/positions/assets/top-10?history=true`);

export const getTop10TradedAssetsLimited = async (): Promise<AxiosResponse<TopListAsset>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/limited/positions/assets/top`);

export const getOpenPositionMetricSummary = async (
    segmentId: number,
): Promise<AxiosResponse<OpenPositionMetricSummary | SegmentHoldingsSummary>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/segments/${segmentId}/positions/summary`);
export const getOpenPositionMetricSummaryLimited = async (
    segmentId: number,
): Promise<AxiosResponse<OpenPositionMetricSummary | SegmentHoldingsSummary>> =>
    axiosInstance.get(
        `${API_ROUTES.INTELLIGENCE_V2}/limited/segments/${segmentId}/positions/summary`,
    );
export const getSegmentHoldingsSummary = async (
    segmentId: number,
): Promise<AxiosResponse<SegmentHoldingsSummary>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/segments/${segmentId}/holdings/summary`);

export const getSegmentHoldingsSummaryLimited = async (
    segmentId: number,
): Promise<AxiosResponse<SegmentHoldingsSummary>> =>
    axiosInstance.get(
        `${API_ROUTES.INTELLIGENCE_V2}/limited/segments/${segmentId}/holdings/summary`,
    );
export const getTopScalpers = async (segmentId): Promise<AxiosResponse<ScalperActivityData>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/segments/${segmentId}/trades/assets/top-10`);

export const getTopScalpersLimited = async (
    segmentId,
): Promise<AxiosResponse<ScalperActivityData>> =>
    axiosInstance.get(
        `${API_ROUTES.INTELLIGENCE_V2}/limited/segments/${segmentId}/trades/assets/top`,
    );
export const getTopCrypto = async (): Promise<AxiosResponse<TopCrypto>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/holdings/assets/top-crypto`);
export const getTopCryptoLimited = async (): Promise<AxiosResponse<TopCrypto>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/limited/holdings/assets/top-crypto`);
export const getAllAlarms = async (): Promise<AxiosResponse<Alarm[]>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/alarms`);
export const getAlarmById = async (id: number): Promise<AxiosResponse<Alarm>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/alarms/${id}`);
export const createNewAlarm = async (data: Alarm): Promise<AxiosResponse<Alarm>> =>
    axiosInstance.post(`${API_ROUTES.INTELLIGENCE_V2}/alarms`, data);
export const updateExistingAlarm = async (payload: Alarm): Promise<AxiosResponse<Alarm>> =>
    axiosInstance.put(`${API_ROUTES.INTELLIGENCE_V2}/alarms/${payload.id}`, payload);
export const deleteExistingAlarm = async (id: number): Promise<AxiosResponse<void>> =>
    axiosInstance.delete(`${API_ROUTES.INTELLIGENCE_V2}/alarms/${id}`);
export const getTopHoldingsAssets = async (): Promise<AxiosResponse<AssetsMostOwned>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/holdings/assets/most-owned`);
export const getTopHoldingsAssetsLimited = async (): Promise<AxiosResponse<AssetsMostOwned>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/limited/holdings/assets/most-owned`);
export const getHoldingsSummary = async (): Promise<AxiosResponse<HoldingsSummary>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/holdings/summary`);
export const getInitialActivityStreamData = async (params: any): Promise<AxiosResponse<any>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/positions/stream`, { params });
export const getInitialActivityStreamDataLimited = async (
    params: any,
): Promise<AxiosResponse<any>> =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/limited/positions/stream`, { params });
export const getActivityStreamSummary = async (params) =>
    axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/positions/stream/summary/last-hour`, {
        params,
    });

export const getCryptoFiatStableMetricsDaily = async (): Promise<
    AxiosResponse<CryptoFiatStableMetricDaily>
> => axiosInstance.get(`${API_ROUTES.INTELLIGENCE_V2}/holdings/asset-categories/metrics/daily`);

export const getCountSegmentTraders = async (
    params: SegmentCriteria,
): Promise<AxiosResponse<CountTradersResponse>> =>
    axiosInstance.post(`${API_ROUTES.INTELLIGENCE_V2}/segments/validate`, params);

export const createSegments = async (
    data: CreateSegmentPayload,
): Promise<AxiosResponse<AllSegmentsIncludingCustom>> =>
    axiosInstance.post(`${API_ROUTES.INTELLIGENCE_V2}/segments`, data);

export const updateSegments = async ({
    id,
    ...data
}: UpdateSegmentPayload): Promise<AxiosResponse<AllSegmentsIncludingCustom>> =>
    axiosInstance.put(`${API_ROUTES.INTELLIGENCE_V2}/segments/${id}`, data);

export const deleteSegments = async (id: number): Promise<AxiosResponse<void>> =>
    axiosInstance.delete(`${API_ROUTES.INTELLIGENCE_V2}/segments/${id}`);
